import { Card } from "@/common/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/projects/IPO/components/ui/table";

interface ValuationTabProps {
  company: any;
}

const ValuationTab = ({ company }: ValuationTabProps) => {
  return (
    <div className="mx-auto max-w-5xl space-y-12 duration-500 animate-in fade-in">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
        <div className="space-y-6">
          <h3 className="text-lg font-bold text-gray-900">
            Historical Valuation
          </h3>
          <Card className="space-y-8 border border-gray-100 p-6 shadow-sm">
            {[
              {
                label: "P/E Comparison (5Y)",
                current: 24.5,
                low: 18.2,
                high: 32.4,
                avg: 26.1,
              },
              {
                label: "Price / Sales (3Y)",
                current: 2.8,
                low: 1.5,
                high: 4.2,
                avg: 2.2,
              },
              {
                label: "Price / Book (10Y)",
                current: 3.2,
                low: 2.1,
                high: 5.8,
                avg: 3.8,
              },
            ].map(stat => (
              <div key={stat.label} className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">
                    {stat.label}
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {stat.current}
                  </span>
                </div>
                <div className="relative h-1 rounded-full bg-gray-100">
                  <div
                    className="absolute h-full rounded-full bg-gray-200"
                    style={{ left: "25%", right: "20%" }}
                  />
                  <div
                    className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-[#00D09C] bg-white shadow-md"
                    style={{
                      left: `${((stat.current - stat.low) / (stat.high - stat.low)) * 100}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between text-[10px] font-medium text-gray-400">
                  <span>Low {stat.low}</span>
                  <span>Avg {stat.avg}</span>
                  <span>High {stat.high}</span>
                </div>
              </div>
            ))}
          </Card>
        </div>

        <div className="space-y-6">
          <h3 className="text-lg font-bold text-gray-900">Peer Valuation</h3>
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <Table>
              <TableHeader className="bg-gray-50/50">
                <TableRow>
                  <TableHead className="py-4 pl-6 text-xs font-bold text-gray-500">
                    Company
                  </TableHead>
                  <TableHead className="py-4 text-right text-xs font-bold text-gray-900">
                    P/E
                  </TableHead>
                  <TableHead className="py-4 text-right text-xs font-bold text-gray-500">
                    P/B
                  </TableHead>
                  <TableHead className="py-4 pr-6 text-right text-xs font-bold text-gray-500">
                    Div. Yield
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  {
                    name: company?.name ?? "N/A",
                    pe: company?.peRatio ?? "—",
                    pb: "3.2",
                    div: "0.41%",
                    active: true,
                  },
                  { name: "TCS", pe: "30.1", pb: "12.5", div: "1.2%" },
                  { name: "HDFC Bank", pe: "18.5", pb: "3.2", div: "0.9%" },
                  { name: "Infosys", pe: "25.0", pb: "7.5", div: "1.5%" },
                ].map(peer => (
                  <TableRow
                    key={peer.name}
                    className={`border-gray-50 ${peer.active ? "bg-[#00D09C]/5" : ""}`}
                  >
                    <TableCell
                      className={`py-4 pl-6 text-sm font-bold ${peer.active ? "text-[#00D09C]" : "text-gray-700"}`}
                    >
                      {peer.name}
                    </TableCell>
                    <TableCell className="py-4 text-right text-sm font-semibold text-gray-900">
                      {peer.pe}
                    </TableCell>
                    <TableCell className="py-4 text-right text-sm font-medium text-gray-500">
                      {peer.pb}
                    </TableCell>
                    <TableCell className="py-4 pr-6 text-right text-sm font-medium text-gray-500">
                      {peer.div}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-6 border-t border-gray-100 pt-12">
        <h3 className="text-lg font-bold text-gray-900">Historical Ratios</h3>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50/50">
                <TableRow>
                  <TableHead className="w-[280px] py-4 pl-6 text-xs font-bold text-gray-500">
                    Particulars
                  </TableHead>
                  {[
                    "Mar 2020",
                    "Mar 2021",
                    "Mar 2022",
                    "Mar 2023",
                    "Mar 2024",
                  ].map(year => (
                    <TableHead
                      key={year}
                      className="py-4 text-right text-xs font-bold text-gray-900"
                    >
                      {year}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {company?.ratios ? (
                  company.ratios.map((row: any) => (
                    <TableRow
                      key={row.COLUMNNAME}
                      className="border-gray-50 transition-colors hover:bg-gray-50/50"
                    >
                      <TableCell className="py-4 pl-6 text-sm font-medium text-gray-600">
                        {row.COLUMNNAME}
                      </TableCell>
                      {[
                        "Y202003",
                        "Y202103",
                        "Y202203",
                        "Y202303",
                        "Y202403",
                      ].map(k => (
                        <TableCell
                          key={k}
                          className="py-4 text-right text-sm font-medium text-gray-900"
                        >
                          {row[k] ?? "—"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="h-32 py-10 text-center text-sm text-gray-400"
                    >
                      Ratios data not available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-5">
        {[
          { label: "Earnings Yield", value: "4.08%" },
          { label: "EV / EBITDA", value: company?.evEbitda ?? "14.5" },
          { label: "Price to Sales", value: "2.84" },
          { label: "PEG Ratio", value: "1.42" },
          { label: "Asset Turnover", value: "0.85" },
        ].map(ratio => (
          <div key={ratio.label} className="space-y-1">
            <p className="text-xs font-medium text-gray-400">{ratio.label}</p>
            <p className="text-sm font-bold text-gray-900">{ratio.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ValuationTab;
